import { SERVICES, MEDIA } from "@/lib/constants";
import { Link } from "react-router-dom";
import {
  Scissors,
  TreePine,
  Trash2,
  Layers,
  Snowflake,
  Sparkles,
  Trees,
  Truck,
  Hammer,
  Droplets,
  Wind,
  ArrowUpRight,
} from "lucide-react";

const ICONS = {
  "lawn-care": Sparkles,
  "lawn-mowing": Scissors,
  "hedge-trimming": Scissors,
  "tree-trimming": TreePine,
  patios: Hammer,
  "junk-removal": Truck,
  "backyard-makeover": Trees,
  "bin-washing": Droplets,
  "snow-removal": Snowflake,
  salting: Layers,
  "snow-plow": Wind,
};

export default function Services({ onQuoteClick }) {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <span className="label-eyebrow">— What we do</span>
            <h2 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1]">
              Eleven services.
              <br />
              <span className="text-[#8AB273]">One trusted crew.</span>
            </h2>
          </div>
          <p className="text-white/60 max-w-md lg:text-right">
            Whether it&apos;s a Tuesday-morning mow or a 4 AM plow,
            we&apos;re the same team showing up — equipped, insured and on time.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-12 gap-5 lg:gap-6 auto-rows-fr">
          {/* Snow plow - big visual */}
          <BigCard
            testId="service-card-snow"
            className="md:col-span-2 lg:col-span-6 lg:row-span-2"
            image={MEDIA.snowPlow}
            title="Snow Plowing"
            tagline="Winter / Year-round"
            description="Driveways, lots and long laneways across the Niagara Region — cleared before sunrise, salted for safety."
            onQuoteClick={onQuoteClick}
          />

          {/* Lawn-care featured */}
          <Card
            testId="service-card-lawn-care"
            className="md:col-span-2 lg:col-span-3 lg:row-span-1"
            highlight
            icon={ICONS["lawn-care"]}
            service={SERVICES.find((s) => s.key === "lawn-care")}
          />
          <Card
            testId="service-card-lawn-mowing"
            className="md:col-span-2 lg:col-span-3 lg:row-span-1"
            icon={ICONS["lawn-mowing"]}
            service={SERVICES.find((s) => s.key === "lawn-mowing")}
          />

          {/* Hedge image card */}
          <ImageCard
            testId="service-card-hedge"
            className="md:col-span-2 lg:col-span-3 lg:row-span-1"
            image={MEDIA.hedge}
            service={SERVICES.find((s) => s.key === "hedge-trimming")}
          />
          <Card
            testId="service-card-tree"
            className="md:col-span-2 lg:col-span-3 lg:row-span-1"
            icon={ICONS["tree-trimming"]}
            service={SERVICES.find((s) => s.key === "tree-trimming")}
          />

          {/* Junk Removal - now featured image card */}
          <ImageCard
            testId="service-card-junk"
            className="md:col-span-4 lg:col-span-8 lg:row-span-1"
            image={MEDIA.junkRemoval}
            service={SERVICES.find((s) => s.key === "junk-removal")}
          />

          {/* Patio image */}
          <ImageCard
            testId="service-card-patio"
            className="md:col-span-2 lg:col-span-4 lg:row-span-1"
            image={MEDIA.patio}
            service={SERVICES.find((s) => s.key === "patios")}
          />
          <ImageCard
            testId="service-card-makeover"
            className="md:col-span-2 lg:col-span-4 lg:row-span-1"
            image={MEDIA.backyard}
            service={SERVICES.find((s) => s.key === "backyard-makeover")}
          />
          <ImageCard
            testId="service-card-bin"
            className="md:col-span-2 lg:col-span-4 lg:row-span-1"
            image={MEDIA.bins}
            service={SERVICES.find((s) => s.key === "bin-washing")}
          />
          <ImageCard
            testId="service-card-snowremoval"
            className="md:col-span-2 lg:col-span-4 lg:row-span-1"
            image={MEDIA.snowRemoval}
            service={SERVICES.find((s) => s.key === "snow-removal")}
          />
          <ImageCard
            testId="service-card-salting"
            className="md:col-span-2 lg:col-span-4 lg:row-span-1"
            image={MEDIA.salting}
            service={SERVICES.find((s) => s.key === "salting")}
          />
        </div>
      </div>
    </section>
  );
}

function Card({ service, icon: Icon, className = "", highlight = false, testId }) {
  if (!service) return null;
  return (
    <Link
      to={`/services/${service.key}`}
      data-testid={testId}
      className={`group relative overflow-hidden rounded-3xl p-6 lg:p-7 border transition-all duration-300 hover:-translate-y-1 block ${
        highlight
          ? "border-[#8AB273]/40 bg-[#172621]"
          : "border-white/8 bg-[#131c17]"
      } ${className}`}
    >
      <div
        className={`h-11 w-11 rounded-xl flex items-center justify-center mb-5 ${
          highlight ? "bg-[#8AB273] text-black" : "bg-white/5 text-[#8AB273]"
        }`}
      >
        {Icon ? <Icon size={20} /> : null}
      </div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display text-xl font-bold tracking-tight flex items-center gap-1.5">
          {service.title}
          <ArrowUpRight
            size={16}
            className="text-[#8AB273] opacity-0 group-hover:opacity-100 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
          />
        </h3>
        <span className="text-[10px] uppercase tracking-[0.18em] text-white/40">
          {service.season}
        </span>
      </div>
      <p className="text-sm text-white/55 leading-relaxed">{service.blurb}</p>
    </Link>
  );
}

function ImageCard({ service, image, className = "", testId }) {
  if (!service) return null;
  return (
    <Link
      to={`/services/${service.key}`}
      data-testid={testId}
      className={`group relative overflow-hidden rounded-3xl border border-white/10 transition-all duration-300 hover:-translate-y-1 min-h-[280px] block ${className}`}
    >
      <img
        src={image}
        alt={service.title}
        loading="lazy"
        decoding="async"
        width="1600"
        height="1200"
        className="absolute inset-0 h-full w-full object-cover scale-105 group-hover:scale-110 transition-transform duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
      <div className="relative h-full p-6 lg:p-7 flex flex-col justify-end">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#8AB273] mb-2">
          {service.season}
        </div>
        <h3 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
          {service.title}
          <ArrowUpRight
            size={18}
            className="text-[#8AB273] opacity-0 group-hover:opacity-100 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
          />
        </h3>
        <p className="text-sm text-white/70 mt-2 max-w-sm leading-relaxed">
          {service.blurb}
        </p>
      </div>
    </Link>
  );
}

function BigCard({
  image,
  title,
  tagline,
  description,
  className = "",
  testId,
  onQuoteClick,
  slug = "snow-plow",
}) {
  return (
    <div
      data-testid={testId}
      className={`relative overflow-hidden rounded-3xl border border-white/10 min-h-[340px] lg:min-h-[520px] group ${className}`}
    >
      <img
        src={image}
        alt={title}
        loading="lazy"
        decoding="async"
        width="1800"
        height="1200"
        className="absolute inset-0 h-full w-full object-cover scale-105 group-hover:scale-110 transition-transform duration-[1200ms]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a1210] via-black/40 to-black/30" />
      <div className="relative h-full p-7 lg:p-10 flex flex-col justify-between">
        <div className="inline-flex w-fit items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 backdrop-blur text-[10px] uppercase tracking-[0.2em]">
          <Snowflake size={12} className="text-[#6EA8D0]" /> {tagline}
        </div>
        <div>
          <h3 className="font-display text-3xl lg:text-5xl font-extrabold tracking-tighter leading-tight max-w-md">
            {title}
          </h3>
          <p className="mt-4 text-white/70 max-w-md leading-relaxed">
            {description}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to={`/services/${slug}`}
              data-testid="big-snow-learn-more"
              className="inline-flex items-center gap-2 bg-white text-black font-semibold px-5 py-3 rounded-full hover:bg-white/90 transition"
            >
              Learn more
            </Link>
            <button
              data-testid="big-snow-cta"
              onClick={onQuoteClick}
              className="inline-flex items-center gap-2 ppc-glass text-white px-5 py-3 rounded-full hover:bg-white/10 transition"
            >
              Book winter coverage
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
